import { PropsWithChildren, useCallback } from 'react';

import { useCreateConsumerStudentFeeCheckout } from '@givebacks/api/store';
import type { ListConsumerStudentsResponse } from '@givebacks/api/student';
import { useListConsumerStudents } from '@givebacks/api/student';
import { useShopRoutes } from '@givebacks/shop';
import { getErrorMessage } from '@givebacks/utils';
import { useMatch } from 'react-router-dom';
import { Box, Container, Group, LoadingOverlay, Stack, Title, notifications } from 'ui';
import { OneToTwoLayoutTemplate } from 'ui/Core';

import { TotalDueCard } from './molecules/cards/TotalDueCard';
import { NoStudents } from './molecules/no-data/NoStudents';
import { StudentFeesRouteSegmentedControl } from './molecules/segmented-controls/RouteSegmentedControl';
import { AddStudentButton } from './organisms/buttons/AddStudentButton';

const getStudentCount = (students?: ListConsumerStudentsResponse) => {
  return students?.meta?.count || 0;
};

const NoStudentsLayout = () => {
  return (
    <Container>
      <Stack>
        <Title order={2}>Student Fees</Title>
        <NoStudents />
      </Stack>
    </Container>
  );
};

/** Returns a component that will a empty state when no students are found */
const StudentsGuard = ({ children }: PropsWithChildren) => {
  const { data: studentCount, isLoading } = useListConsumerStudents({
    pagination: { limit: 1 },
    select: getStudentCount,
  });
  if (isLoading) {
    return <LoadingOverlay visible />;
  }
  if (!studentCount) {
    return <NoStudentsLayout />;
  }
  return children;
};

/**
 * Add Student Guard only displays Add student button on the student view
 */
const AddStudentGuard = ({ size = 'sm' }: { size?: 'sm' | 'md' }) => {
  const isStudentView = useMatch('student-fees/students');
  if (!isStudentView) {
    return null;
  }
  return <AddStudentButton size={size} />;
};

const getTotalDue = (students: ListConsumerStudentsResponse) => {
  return students?.unpaid_fee_total;
};

const TotalDueForUserCard = () => {
  const { data: totalDue } = useListConsumerStudents({
    select: getTotalDue,
    queryOptions: {
      options: {
        include: 'unpaid_fee_total',
      },
    },
  });

  const { navigateToCheckout } = useShopRoutes();
  const { mutate: createConsumerStudentFeeCheckout, isPending: isCreatingStudentFeeCheckout } =
    useCreateConsumerStudentFeeCheckout();

  const onPayNowClick = useCallback(() => {
    // Create a consumer checkout cart with the results and
    // navigate to the checkout page
    createConsumerStudentFeeCheckout(
      {},
      {
        onSuccess: (response) => {
          navigateToCheckout(response.checkout.uuid);
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage(error, 'Error creating student fee checkout'),
            variant: 'error',
          });
        },
      }
    );
  }, [createConsumerStudentFeeCheckout, navigateToCheckout]);

  return (
    <TotalDueCard
      totalDue={totalDue}
      withPayNowButton={!!totalDue && totalDue > 0}
      buttonProps={{ onClick: onPayNowClick, loading: isCreatingStudentFeeCheckout }}
    />
  );
};

type StudentFeesLayoutProps = PropsWithChildren;
const StudentFeesLayout = ({ children }: StudentFeesLayoutProps) => {
  return (
    <StudentsGuard>
      <OneToTwoLayoutTemplate
        leftSection={
          <Stack>
            <Box hiddenFrom="md">
              <Title order={2}>Student Fees</Title>
            </Box>
            <TotalDueForUserCard />
          </Stack>
        }
        rightSection={
          <Stack>
            <Group justify="space-between" visibleFrom="md">
              <Stack flex={1}>
                <Title order={2}>Student Fees</Title>
              </Stack>

              <AddStudentGuard size={'sm'} />
            </Group>

            <StudentFeesRouteSegmentedControl />
            {/* Set a relative box so that loading overlay is centered  on following elements*/}
            <Box pos="relative" mih={300}>
              {children}
            </Box>
          </Stack>
        }
        mobileBottomButton={<AddStudentGuard size={'md'} />}
      />
    </StudentsGuard>
  );
};

export { StudentFeesLayout };
